/**
 * Standardized API Routes
 * 
 * This file defines all API routes with consistent parameter naming conventions:
 * - Resource-specific IDs: actorId, portfolioId, characterId, variationId
 * - Nested resources use parent resource ID in the path
 */

// Import base URL from config
import { config } from '../../constants/config';

const API_BASE_URL = config.BASE_URL;

export const apiRoutes = {
  // Voice Actor User Routes
  VOICE_ACTORS: {
    BASE: `${API_BASE_URL}/voice-actors`,
    DETAIL: `${API_BASE_URL}/voice-actors/:actorId`,
    PORTFOLIOS: `${API_BASE_URL}/voice-actors/:actorId/portfolios`,
    ASSIGNMENTS: `${API_BASE_URL}/voice-actors/:actorId/assignments`,
    UPLOAD_IMAGE: `${API_BASE_URL}/voice-actors/:actorId/image`,
    RECOMMENDED_OPPORTUNITIES: `${API_BASE_URL}/voice-actors/:actorId/recommended-opportunities`,
    APPLICATIONS: `${API_BASE_URL}/voice-actors/:actorId/applications`,
    SCHEMA: `${API_BASE_URL}/voice-actors/schema`
  },

  // Portfolio Routes
  PORTFOLIOS: {
    BASE: `${API_BASE_URL}/portfolios`,
    DETAIL: `${API_BASE_URL}/portfolios/:portfolioId`,
    CHARACTERS: `${API_BASE_URL}/portfolios/:portfolioId/characters`
  },

  // Character Routes
  CHARACTERS: {
    BASE: `${API_BASE_URL}/characters`,
    DETAIL: `${API_BASE_URL}/characters/:characterId`,
    VARIATIONS: `${API_BASE_URL}/characters/:characterId/variations`,
    AVATAR: `${API_BASE_URL}/characters/:characterId/avatar`,
    AVATAR_PRE: `${API_BASE_URL}/characters/avatar/pre`,
    OPPORTUNITIES: `${API_BASE_URL}/characters/:characterId/opportunities`,
    ALL_VARIATIONS: `${API_BASE_URL}/characters/:characterId/all-variations`,
    WITH_VARIATIONS: `${API_BASE_URL}/characters/with-variations`
  },

  // Variation Routes
  VARIATIONS: {
    BASE: `${API_BASE_URL}/variations`,
    DETAIL: `${API_BASE_URL}/variations/:variationId`,
    AUDIO: `${API_BASE_URL}/variations/:variationId/audio`
  },

  // Assignment Routes
  ASSIGNMENTS: {
    BASE: `${API_BASE_URL}/assignments`,
    DETAIL: `${API_BASE_URL}/assignments/:assignmentId`
  },

  // Opportunity Routes
  OPPORTUNITIES: {
    BASE: `${API_BASE_URL}/opportunities`,
    DETAIL: `${API_BASE_URL}/opportunities/:opportunityId`,
    APPLICATIONS: `${API_BASE_URL}/opportunities/:opportunityId/applications`
  },

  // Application Routes
  APPLICATIONS: {
    BASE: `${API_BASE_URL}/applications`,
    DETAIL: `${API_BASE_URL}/applications/:applicationId`
  },

  // Book Routes
  BOOKS: {
    BASE: `${API_BASE_URL}/books`,
    DETAIL: `${API_BASE_URL}/books/:bookId`,
    PROFILE: `${API_BASE_URL}/books/:bookId/profile`,
    COMPLETE: `${API_BASE_URL}/books/:bookId/complete`,
    OPPORTUNITIES: `${API_BASE_URL}/books/:bookId/opportunities`,
    CHARACTERS: `${API_BASE_URL}/books/:bookId/characters`,
    BY_USER: `${API_BASE_URL}/get-book-full-data`,
    ALL_BY_USER: `${API_BASE_URL}/get-books-by-user`,
    UPLOAD_SOURCE: `${API_BASE_URL}/upload-book-source/:userId/:bookUuid`,

    // Story Import Routes
    IMPORT_STORY: `${API_BASE_URL}/books/:bookId/import-story`,
    STORY_IMPORT_STATUS: `${API_BASE_URL}/books/:bookId/story-import-status`,
    IMPORTED_CHARACTERS: `${API_BASE_URL}/books/:bookId/imported-characters`
  },

  // Literary Group Routes
  LIT_GROUPS: {
    BASE: `${API_BASE_URL}/direct/lit-groups`,
    DETAIL: `${API_BASE_URL}/direct/lit-groups/:litGroupId`,
    PROFILE: `${API_BASE_URL}/direct/lit-groups/:litGroupId/profile`,
    BOOKS: `${API_BASE_URL}/direct/lit-groups/:litGroupId/books`,
    AUTHORS: `${API_BASE_URL}/direct/lit-groups/:litGroupId/authors`
  },

  // Composite Data
  COMPOSITE: {
    VOICE_LAB: `${API_BASE_URL}/voice-lab/composite-data`
  },

  // User Routes
  USERS: {
    PROFILES: `${API_BASE_URL}/users/:userId/profiles`,
    PRIMARY_ROLE: `${API_BASE_URL}/users/:userId/primary-role`,
    PROFILE_IMAGE: `${API_BASE_URL}/upload-profile-image/:userId`
  },

  // Authentication Routes
  AUTH: {
    LOGIN: `${API_BASE_URL}/login`,
    REGISTER: `${API_BASE_URL}/register`,
    LOGGED_IN_USER: `${API_BASE_URL}/logged-in-user`,
    CREATE_USER_META: `${API_BASE_URL}/create-user-meta`,
    CREATE_USER_S3_FOLDERS: `${API_BASE_URL}/create-user-s3-folders`
  },

  // Utility Routes
  UTILS: {
    GET_SIGNED_URL_BY_TYPE: `${API_BASE_URL}/get-signed-url-by-type`,
    GET_SIGNED_URL_BY_PATH: `${API_BASE_URL}/get-signed-url-by-path`,
    GET_SIGNED_URL_BY_RAW_PATH: `${API_BASE_URL}/get-signed-url-by-raw-path`,
    PATH_DEFINITIONS: `${API_BASE_URL}/path-definitions`,
    MOVE_FILE: `${API_BASE_URL}/move-file/aai-api`,
    UPLOAD_TEMP_IMAGE: `${API_BASE_URL}/upload-temp-image/temp`,
    UPLOAD_AUDIO_SNIPPET: `${API_BASE_URL}/upload-audio-snippet/:userId`,
    UPLOAD_TEXT_SNIPPET: `${API_BASE_URL}/upload-text-snippet/:userId/:fileName`
  },

  // Snippet Routes
  SNIPPETS: {
    TEXT: `${API_BASE_URL}/text-snippets/:userId/:uuid/:s3TextUrl`,
    VOICE: `${API_BASE_URL}/voice-snippets/:userId/:uuid/:s3VoiceUrl`,
    TEXT_VOICE_PAIR: `${API_BASE_URL}/text-voice-snippet-pair`,
    TEXT_VOICE_PAIRS: `${API_BASE_URL}/text-voice-snippet-pairs/:userId`,
    TEXT_CLONE: `${API_BASE_URL}/text-clone-snippets/:userId/:folderName/:fileName`,
    VOICE_CLONE: `${API_BASE_URL}/voice-clone-snippets/:userId/:folderName/:fileName`,
    TEXT_OVERRIDE: `${API_BASE_URL}/add-text-snippet-override`,
    GET_TEXT_OVERRIDE: `${API_BASE_URL}/get-text-snippet-override/:userId/:uuid`
  },

  // Character Speech Sample Routes
  SPEECH_SAMPLES: {
    ALL: `${API_BASE_URL}/char-speech-samples/:userId`,
    DETAIL: `${API_BASE_URL}/text-voice-sample/:userId/:csUuid`,
    DELETE: `${API_BASE_URL}/delete-character_speech_sample/:targetUuid`,
    DELETE_DETAILS: `${API_BASE_URL}/delete-voice-sample-details/:userId/:csUuid`,
    ADD: `${API_BASE_URL}/add-character_speech_sample`,
    ADD_EMPTY: `${API_BASE_URL}/add-empty-character_speech_sample`,
    CHAR_VAR_SAMPLES: `${API_BASE_URL}/get-char-var-samples`,
    BOOK_CHAR_VAR_SAMPLES: `${API_BASE_URL}/get-book-char-var-samples`,
    HIGHEST_REVISION: `${API_BASE_URL}/get-highest_revision`
  },

  // Article Routes
  ARTICLES: {
    ADD: `${API_BASE_URL}/add-new-articleitem`,
    DELETE: `${API_BASE_URL}/delete-articleitem/:uuid/:s3ArticleUrl`,
    GET: `${API_BASE_URL}/get-articleitem`,
    GET_ALL: `${API_BASE_URL}/get-articles`
  }
};

export default apiRoutes;
